import { useLoanQuotePresentation } from '@axo/shared/data-access/hooks';
import { EnrichedLoanQuote } from '@axo/shared/data-access/types/loan_quote_presentation';
import { useCallback, useMemo } from 'react';

export interface QuoteRanking {
  value: number;
  ordinal: string;
}

export function useGetQuoteMyPageRanking(applicationID?: string) {
  const { data: loanQuotePresentationData } =
    useLoanQuotePresentation(applicationID);

  const quotesRanking = useMemo(() => {
    const rankings: Record<string, QuoteRanking> = {};
    let maxRank = 0;

    const ordinalRules = new Intl.PluralRules('en', {
      type: 'ordinal',
    });

    const suffixes: Record<Intl.LDMLPluralRule, string> = {
      one: 'st',
      two: 'nd',
      few: 'rd',
      other: 'th',
      zero: 'th',
      many: 'th',
    };

    function toOrdinal(n: number) {
      const category = ordinalRules.select(n);
      const suffix = suffixes[category];
      return n + suffix;
    }

    function addQuoteRanking(quote: EnrichedLoanQuote) {
      maxRank++;
      rankings[quote.ID] = { ordinal: toOrdinal(maxRank), value: maxRank };
    }

    if (loanQuotePresentationData) {
      loanQuotePresentationData.AcceptedLoanQuotes?.forEach(addQuoteRanking);
      loanQuotePresentationData.AcceptableLoanQuotes?.forEach(addQuoteRanking);
      loanQuotePresentationData.CancelledLoanQuotes?.forEach(addQuoteRanking);
    }

    return rankings;
  }, [loanQuotePresentationData]);

  const getter = useCallback(
    (quoteId?: string) => {
      return quoteId ? quotesRanking[quoteId] : undefined;
    },
    [quotesRanking],
  );

  return getter;
}
